import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { PDFDocument } from 'pdf-lib';
import { Box, Button, Flex, useToast } from '@chakra-ui/react';
import UploadComponent from './UploadComponent';
import RSelect from '../form/RSelect';
import { TState } from '../../redux/store';
import Loader from '../common/Loader';
import { useHandleUpload } from '../tabs/SubTabs';
import { entries, keyBy } from 'lodash';
import { mergeWithArray } from '../../helpers';

const PDFSplit = ({ handleRefresh }: { handleRefresh: () => void }) => {
  const documentList = useSelector((state: TState) => state.documentList);
  const theme = useSelector((state: TState) => state.theme);
  const toast = useToast();
  const handleUpload = useHandleUpload();

  const [files, setFiles] = useState<any[]>([]);
  const [documentSelected, setDocumentSelected] = useState<
    Record<number, string>
  >({});
  const [loading, setLoading] = useState(false);

  const docById = keyBy(documentList, '_id');

  const handleFileDrop = useCallback(async (acceptedFiles: FileList) => {
    try {
      setFiles([]);
      setLoading(true);
      for (const file of acceptedFiles) {
        if (file.type !== 'application/pdf') {
          toast({ title: "Le fichier n'est pas un PDF", status: 'error' });
          continue;
        }

        const arrayBuffer = await file.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);

        const totalPages = pdfDoc.getPageCount();

        totalPages === 0 &&
          toast({ title: 'Le fichier est vide', status: 'error' });

        const blobs = [] as any[];

        for (let i = 0; i < totalPages; i++) {
          const newPdf = await PDFDocument.create();
          const [copiedPage] = await newPdf.copyPages(pdfDoc, [i]);
          newPdf.addPage(copiedPage);

          const pdfBytes = await newPdf.save();
          const blob = new Blob([pdfBytes], { type: 'application/pdf' });
          const file = new File([blob], 'blob.pdf', {
            type: 'application/pdf',
          });

          blobs.push(file);
        }

        setFiles((f) => [...f, ...blobs]);
        setLoading(false);
      }
    } catch (error: any) {
      /No PDF header found/gi.test(error)
        ? toast({
            title: "Le fichier n'est pas un PDF valide",
            status: 'error',
          })
        : console.error('PDFSplit error: ', error);
    }
  }, []);

  const handleChange = (e: any, idx: number) => {
    setDocumentSelected((ds) => ({ ...ds, [idx]: e?.value }));
  };

  const handleSubmit = async () => {
    const uploads = entries(documentSelected).reduce(
      (prev, [fileIndex, documentId]) => {
        const file = new File(
          [files[fileIndex as any]],
          `${docById[documentId].name}.pdf`,
          {
            type: 'application/pdf',
          },
        );

        return !documentId
          ? prev
          : mergeWithArray(prev, { [documentId]: [file] });
      },
      {},
    );

    const promises = entries(uploads).map(([id, files]) => {
      return handleUpload(files as FileList, id);
    });

    await Promise.all(promises);

    setFiles([]);
    setDocumentSelected({});

    handleRefresh();
  };

  const urls = useMemo(
    () => files.map((file) => URL.createObjectURL(file)),
    [files],
  );

  return (
    <>
      <Box my={2}>
        <UploadComponent
          onUpload={handleFileDrop}
          multiple
          label='Fichier PDF à découper'
          bgColor={theme.dark}
          color={theme.none}
        />
      </Box>
      {loading && <Loader />}
      <Flex gap={2} flexWrap='wrap'>
        {files.map((_, fileIdx) => (
          <Flex direction='column'>
            <iframe src={urls[fileIdx]} height='500px'></iframe>
            <RSelect
              options={documentList.map((option) => ({
                label: option.name,
                value: option._id,
              }))}
              onChange={(e) => handleChange(e, fileIdx)}
            />
          </Flex>
        ))}
      </Flex>
      {files.length > 0 && (
        <Button mt={2} onClick={handleSubmit}>
          Valider l'import
        </Button>
      )}
    </>
  );
};

export default PDFSplit;
